import { useRef, useState } from "react";
import {
  GlobalBusyScope,
  useBusyIndicator,
} from "../contexts/BusyIndicatorContext";
import { useError } from "../contexts/ErrorContext";
import type { BasePageOptions } from "../models/BasePageOptions";
import type { IBasePage } from "../models/IBasePage";

export function useBasePage<TState>(
  initialState: TState,
  options?: BasePageOptions,
): IBasePage<TState> {
  const initialStateReference = useRef<TState>(initialState);
  const [state, setState] = useState<TState>(initialStateReference.current);
  const {
    showBusyIndicator: showCentralBusyIndicator,
    closeBusyIndicator: closeCentralBusyIndicator,
  } = useBusyIndicator();
  const { handleError: handleCentralError } = useError();

  const setStateProperty = <K extends keyof TState>(
    property: K,
    value: TState[K],
  ): void => {
    setState((previousState) => ({
      ...previousState,
      [property]: value,
    }));
  };

  const setStateProperties = (values: Partial<TState>): void => {
    setState((previousState) => ({
      ...previousState,
      ...values,
    }));
  };

  const resetState = (): void => {
    setState(initialStateReference.current);
  };

  const showBusyIndicator = (message?: string): void => {
    showCentralBusyIndicator({
      scope: GlobalBusyScope,
      message: message ?? options?.busyMessage,
    });
  };

  const closeBusyIndicator = (): void => {
    closeCentralBusyIndicator(GlobalBusyScope);
  };

  const handleError = (error: unknown): void => {
    if (options?.handleError) {
      options.handleError(error);
      return;
    }

    handleCentralError(error);
  };

  return {
    state,
    setState,
    setStateProperty,
    setStateProperties,
    resetState,
    showBusyIndicator,
    closeBusyIndicator,
    handleError,
  };
}
